import React, { useState, useEffect } from 'react';
import { getContextualError } from '../utils/errorMessages';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLocale } from '../context/LocaleContext';
import { workAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, CheckCircle, FileText, Trash2, Loader2, Upload, Sparkles, Music, Video, Crown, CreditCard, LogOut, User, Receipt, ListTodo, Download, ArrowLeftRight, Search } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import EditProfileModal from '../components/EditProfileModal';
import PaymentHistoryModal from '../components/PaymentHistoryModal';
// NEW: Import enhanced components
// DocumentAnalytics removed per user request
import TemplateSelector from '../components/TemplateSelector';
import ExportModal from '../components/ExportModal';
import DocumentCompareModal from '../components/DocumentCompareModal';
import EnhancedTaskCard from '../components/EnhancedTaskCard';
import DocumentSearch from '../components/DocumentSearch';
import TodaysFocusDashboard from '../components/TodaysFocusDashboard';
// TaskProductivityAnalytics removed per user request
// TaskFilters removed per user request
// TaskTemplates removed
import QuickActionButton from '../components/QuickActionButton';
import BatchOperations from '../components/BatchOperations';
// 🧠 Emotional Mode Awareness
import useEmotionalMode from '../hooks/useEmotionalMode';

const WorkMode = () => {
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  const { toast } = useToast();
  const { t, locale } = useLocale();
  // 🧠 Emotional Mode Hook
  const { emotionalMode, ambientEmotional, shouldShowUrgentLanguage, shouldShowStreaks } = useEmotionalMode();
  const [subscription, setSubscription] = React.useState(null);
  const [tasks, setTasks] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [docDialogOpen, setDocDialogOpen] = useState(false);
  const [processingDoc, setProcessingDoc] = useState(false);
  const [assistantMessage, setAssistantMessage] = useState('');
  const [assistantResponse, setAssistantResponse] = useState(null);
  const [processingAssistant, setProcessingAssistant] = useState(false);
  const [editDocDialogOpen, setEditDocDialogOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [docSummary, setDocSummary] = useState(null);
  const [documentText, setDocumentText] = useState('');
  const [urlInput, setUrlInput] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  const [currentAnalyzedDoc, setCurrentAnalyzedDoc] = useState(null);
  const [showQA, setShowQA] = useState(false);
  const [qaQuestion, setQaQuestion] = useState('');
  const [qaAnswer, setQaAnswer] = useState('');
  const [processingQA, setProcessingQA] = useState(false);
  const [qaHistory, setQaHistory] = useState([]);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    priority: 'medium',
    dueDate: '',
    dueTime: '',
    status: 'pending',
    category: 'work'
  });
  // NEW: Enhanced feature states
  const [selectedTemplate, setSelectedTemplate] = useState('auto');
  const [showExportModal, setShowExportModal] = useState(false);
  const [showFullTranscript, setShowFullTranscript] = useState(false);
  const [exportingDoc, setExportingDoc] = useState(null);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [selectedDocsForCompare, setSelectedDocsForCompare] = useState([]);
  const [showSearchModal, setShowSearchModal] = useState(false);
  // Task Templates removed

  useEffect(() => {
    loadData();
    loadSubscription();
  }, []);

  const loadSubscription = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/subscription`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSubscription(data);
      }
    } catch (error) {
      console.error('Error loading subscription:', error);
    }
  };

  const loadData = async () => {
    try {
      const [tasksResponse, docsResponse] = await Promise.all([
        workAPI.getTasks(),
        workAPI.getDocuments()
      ]);
      setTasks(tasksResponse.data || []);
      setDocuments(docsResponse.data || []);
    } catch (error) {
      console.error('Error loading work data:', error);
      setTasks([]);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const loadTasks = loadData; // Alias for backward compatibility
  
  const loadDocuments = async () => {
    try {
      const docsResponse = await workAPI.getDocuments();
      setDocuments(docsResponse.data || []);
    } catch (error) {
      console.error('Error loading documents:', error);
    }
  };

  const handleAddTask = async () => {
    if (!newTask.title.trim()) {
      toast({
        title: t('work.error'),
        description: t('work.pleaseEnterTitle'),
        variant: 'destructive'
      });
      return;
    }

    try {
      console.log('✅ Adding task:', newTask);
      // Send the date and time as separate fields - backend will combine them
      await workAPI.createTask({
        ...newTask,
        dueDate: newTask.dueDate || null,  // Keep date as YYYY-MM-DD string
        dueTime: newTask.dueTime || null   // Time as HH:mm string
      });
      console.log('✅ Task added successfully');
      toast({
        title: '✅ ' + t('work.taskTitle'),
        description: `"${newTask.title}" added`
      });
      setTaskDialogOpen(false);
      setNewTask({
        title: '',
        description: '',
        priority: 'medium',
        dueDate: '',
        dueTime: '',
        status: 'pending',
        category: 'work'
      });
      await loadTasks();
    } catch (error) {
      console.error('❌ Error creating task:', error);
      toast({
        title: t('work.error'),
        description: getContextualError(error, 'work.createTask'),
        variant: 'destructive'
      });
      // Close dialog anyway after showing error
      setTaskDialogOpen(false);
      setNewTask({
        title: '',
        description: '',
        priority: 'medium',
        dueDate: '',
        dueTime: '',
        status: 'pending',
        category: 'work'
      });
    }
  };

  const handleUpdateTask = async () => {
    if (!newTask.title.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a task title',
        variant: 'destructive'
      });
      return;
    }

    try {
      console.log('✅ Updating task:', editingTask.id, newTask);
      await workAPI.updateTask(editingTask.id, {
        title: newTask.title,
        description: newTask.description,
        priority: newTask.priority,
        dueDate: newTask.dueDate || null,
        dueTime: newTask.dueTime || null
      });
      console.log('✅ Task updated successfully');
      toast({
        title: '✅ Task Updated!',
        description: `"${newTask.title}" has been updated`
      });
      setTaskDialogOpen(false);
      setEditingTask(null);
      setNewTask({
        title: '',
        description: '',
        priority: 'medium',
        dueDate: '',
        dueTime: '',
        status: 'pending',
        category: 'work'
      });
      await loadTasks();
    } catch (error) {
      console.error('❌ Error updating task:', error);
      toast({
        title: 'Error',
        description: getContextualError(error, 'work.updateTask'),
        variant: 'destructive'
      });
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setUploadedFileName(file.name);
      
      // For audio/video files, show helpful message
      if (file.name.match(/\.(mp3|mp4|wav|m4a)$/i)) {
        toast({
          title: '🎵 Audio/Video File Selected',
          description: `${file.name} - Ready for AI transcription & analysis`,
          duration: 5000
        });
      } else if (file.name.match(/\.(pdf|txt|doc|docx)$/i)) {
        toast({
          title: '📄 Document Selected',
          description: `${file.name} - Ready for AI analysis`,
          duration: 5000
        });
      }
    }
  };

  const handleProcessDocument = async () => {
    if (!documentText.trim() && !selectedFile && !urlInput.trim()) {
      toast({
        title: 'Error',
        description: 'Please provide text, upload a file, or enter a URL',
        variant: 'destructive'
      });
      return;
    }

    setProcessingDoc(true);
    try {
      // Create FormData for file upload
      const formData = new FormData();
      
      if (documentText) {
        formData.append('text', documentText);
      }
      
      if (urlInput) {
        formData.append('url', urlInput);
      }
      
      if (selectedFile) {
        formData.append('file', selectedFile);
      }
      
      // NEW: Add template selection (null/auto means AI auto-detects)
      if (selectedTemplate && selectedTemplate !== 'auto') {
        formData.append('template', selectedTemplate);
      }
      
      // Add user's language preference for response
      formData.append('language', locale?.language || 'en');
      
      // Call backend AI analysis endpoint
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/analyze-document`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 402) {
          // Insufficient credits - show prominent error
          const message = errorData.detail?.message || 'You need 5 credits for document analysis.';
          toast({
            title: '❌ Insufficient Credits',
            description: message,
            variant: 'destructive',
            action: {
              label: 'Get Credits',
              onClick: () => navigate('/pricing')
            }
          });
          setProcessingDoc(false);
          return; // Exit early
        }
        
        // Check if it's a YouTube transcript error with helpful instructions
        const errorDetail = errorData.detail || '';
        if (errorDetail.includes('YouTube') || errorDetail.includes('transcript')) {
          // Show the helpful YouTube error message
          toast({
            title: '📺 YouTube Video Notice',
            description: 'YouTube is blocking automated requests. Please copy the transcript manually from YouTube, or paste the video text directly.',
            variant: 'default',
            duration: 10000
          });
          setProcessingDoc(false);
          return;
        }
        
        throw new Error(errorData.detail || 'Failed to analyze document. Please try again.');
      }
      
      const result = await response.json();
      
      console.log('✅ AI Response received:', result);
      console.log('📝 Response content:', result.response);
      
      if (!result.response || result.response.trim().length === 0) {
        throw new Error('AI returned empty response. Please try again.');
      }
      
      // CRITICAL: Set the summary FIRST so it displays immediately
      console.log('🎯 Setting docSummary state...');
      // Store FULL result object including original_content for Q&A
      setDocSummary({
        response: result.response,
        original_content: result.original_content || documentText || '',
        template: result.template,
        is_audio: result.is_audio,
        speakers: result.speakers,
        action_items: result.action_items
      });
      console.log('✅ docSummary state set successfully with original_content');
      
      // Store in currentAnalyzedDoc for Q&A feature
      setCurrentAnalyzedDoc({
        summary: result.response,
        content: result.original_content || documentText || '',
        title: uploadedFileName || urlInput || 'Document Analysis'
      });
      
      // Rest of the processing happens in background - if it fails, user still sees AI results
      try {
        // Parse AI response to extract components with improved regex
        const summaryMatch = result.response.match(/\*\*Summary:\*\*\s*([\s\S]*?)(?=\*\*(?:Key Points|Insights|Micro Tasks):|$)/i);
        const keyPointsMatch = result.response.match(/\*\*Key Points:\*\*\s*([\s\S]*?)(?=\*\*(?:Insights|Micro Tasks):|$)/i);
        const insightsMatch = result.response.match(/\*\*Insights:\*\*\s*([\s\S]*?)(?=\*\*(?:Micro Tasks|Next Step):|$)/i);
        const tasksSection = result.response.match(/\*\*Micro Tasks:\*\*\s*([\s\S]*?)(?=\*\*Next Step:|$)/i)?.[1];
        
        // Extract and clean summary
        let summary = 'AI analysis completed';
        if (summaryMatch && summaryMatch[1]) {
          summary = summaryMatch[1].trim();
          // Remove bullet points for summary text if needed
          const summaryLines = summary.split('\n').filter(line => line.trim());
          if (summaryLines.every(line => line.trim().startsWith('-'))) {
            summary = summaryLines.map(line => line.replace(/^-\s*/, '').trim()).join(' ');
          }
        }
        
        // Extract key points as array
        const keyPointsText = keyPointsMatch ? keyPointsMatch[1].trim() : '';
        const keyPoints = keyPointsText
          .split('\n')
          .filter(line => line.trim().startsWith('-') || line.trim().match(/^\d+\./))
          .map(line => line.replace(/^[-\d.]\s*/, '').trim())
          .filter(point => point.length > 0);
        
        // Extract insights
        const insights = insightsMatch ? insightsMatch[1].trim() : '';
        
        // Determine document type and source (used when saving later)
        const docType = selectedFile ? 'file' : (urlInput ? 'url' : 'text');
        const docSource = selectedFile ? uploadedFileName : (urlInput || 'Direct text input');
        const docTitle = uploadedFileName || urlInput || 'Document Analysis';
        
        // Store document info for later saving (don't auto-save)
        // User will click "Save & Done" to save to library
        
        // Auto-extract and create tasks
        let createdCount = 0;
        if (tasksSection) {
          const taskLines = tasksSection.split('\n').filter(line => line.trim().startsWith('-') || line.trim().match(/^\d+\./));
          
          for (const line of taskLines) {
            const taskTitle = line.replace(/^[-\d.]\s*/, '').trim();
            if (taskTitle) {
              try {
                await workAPI.createTask({
                  title: taskTitle,
                  description: `From: ${docTitle}`,
                  priority: 'medium',
                  status: 'pending',
                  category: 'work'
                });
                createdCount++;
              } catch (err) {
                console.error('Error creating task:', err);
                // Continue with other tasks even if one fails
              }
            }
          }
          
          if (createdCount > 0) {
            loadTasks();
            toast({
              title: '🎯 Tasks Auto-Created!',
              description: `${createdCount} micro-tasks generated from analysis`
            });
          }
        }

        toast({
          title: '✅ Analysis Complete!',
          description: `Saved: Summary, ${keyPoints.length} key points, ${createdCount} tasks`
        });
      } catch (parseError) {
        console.error('Error parsing/saving results:', parseError);
        // Don't clear docSummary - user should still see raw AI response
        toast({
          title: 'Analysis Complete',
          description: 'AI results displayed. Some features may not have saved properly.',
          variant: 'default'
        });
      }
    } catch (error) {
      console.error('Error processing document:', error);
      
      // Only clear if we didn't get a response at all
      setDocSummary(null);
      
      // Show user-friendly error
      let errorMessage = 'Failed to process document. Please try again.';
      if (error.message.includes('credits')) {
        errorMessage = error.message;
      } else if (error.message.includes('Invalid response')) {
        errorMessage = 'AI service returned invalid data. Please try again or contact support.';
      }
      
      toast({
        title: 'Analysis Failed',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setProcessingDoc(false);
    }
  };

  const handleGenerateMicrotasks = async () => {
    if (!docSummary) return;
    
    try {
      // Get the response text from docSummary
      const responseText = typeof docSummary === 'string' ? docSummary : docSummary?.response || '';
      
      // Extract microtasks from the analysis
      const tasksSection = responseText.match(/\*\*Micro Tasks:\*\*\s*([\s\S]*?)(?=\*\*Next Step:|$)/i)?.[1];
      
      if (!tasksSection) {
        toast({
          title: 'No Tasks Found',
          description: 'The analysis doesn\'t contain actionable tasks to generate.',
          variant: 'default'
        });
        return;
      }
      
      const taskItems = tasksSection
        .split('\n')
        .filter(line => line.trim().startsWith('-') || line.trim().match(/^\d+\./))
        .map(line => line.replace(/^[-\d.]\s*/, '').trim())
        .filter(item => item.length > 5);
      
      if (taskItems.length === 0) {
        toast({
          title: 'No Tasks Found',
          description: 'Could not extract actionable tasks from the analysis.',
          variant: 'default'
        });
        return;
      }
      
      let createdCount = 0;
      for (const taskText of taskItems) {
        try {
          const taskData = {
            title: taskText.substring(0, 100),
            description: `Generated from document analysis:\n${currentAnalyzedDoc?.source || 'Document'}`,
            priority: 'medium',
            dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            status: 'pending',
            category: 'work',
            source: 'ai_analysis'
          };
          
          await workAPI.createTask(taskData);
          createdCount++;
        } catch (err) {
          console.error('Error creating task:', err);
        }
      }
      
      if (createdCount > 0) {
        loadTasks();
        toast({
          title: '✅ Tasks Created!',
          description: `${createdCount} microtasks added to your task list`
        });
      }
    } catch (error) {
      console.error('Error generating microtasks:', error);
      toast({
        title: 'Error',
        description: 'Failed to generate tasks. Please try again.',
        variant: 'destructive'
      });
    }
  };

  const handleAskQuestion = async () => {
    if (!qaQuestion.trim() || !docSummary) return;
    
    setProcessingQA(true);
    try {
      // Get full document content for Q&A
      const fullContent = docSummary?.original_content || currentAnalyzedDoc?.content || documentText || '';
      const summaryText = typeof docSummary === 'string' ? docSummary : docSummary?.response || '';
      
      // Send question to backend with FULL document content
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/document-qa`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: qaQuestion,
          document_context: summaryText,
          document_text: fullContent  // Send FULL content, not truncated
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 402) {
          toast({
            title: '❌ Insufficient Credits',
            description: errorData.detail?.message || 'You need 1 credit to ask questions.',
            variant: 'destructive'
          });
          setProcessingQA(false);
          return;
        }
        throw new Error(errorData.detail || 'Failed to get answer');
      }
      
      const result = await response.json();
      
      // Add to Q&A history
      setQaHistory(prev => [...prev, {
        question: qaQuestion,
        answer: result.response || result.answer
      }]);
      
      // Clear input
      setQaQuestion('');
      
      toast({
        title: '✅ Answer Ready',
        description: 'Check the Q&A section below'
      });
    } catch (error) {
      console.error('Error asking question:', error);
      toast({
        title: 'Error',
        description: getContextualError(error, 'work.askQuestion'),
        variant: 'destructive'
      });
    } finally {
      setProcessingQA(false);
    }
  };

  const handleCompleteTask = async (task) => {
    try {
      await workAPI.updateTask(task.id, { status: 'completed' });
      toast({
        title: '✅ Completed!',
        description: `"${task.title}" marked as done`
      });
      loadTasks();
    } catch (error) {
      console.error('Error completing task:', error);
    }
  };

  const handleDeleteTask = async (task) => {
    try {
      await workAPI.deleteTask(task.id);
      toast({
        title: '🗑️ Deleted',
        description: `"${task.title}" removed`
      });
      loadTasks();
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  const handleDeleteDocument = async (doc) => {
    try {
      await workAPI.deleteDocument(doc.id);
      toast({
        title: '🗑️ Document Deleted',
        description: `"${doc.title}" removed from your workspace`
      });
      loadData();
    } catch (error) {
      toast({ title: 'Unable to Delete', description: getContextualError(error, 'work.analyzeDocument'), variant: 'destructive' });
    }
  };

  const handleEditDocument = (doc) => {
    setEditingDoc(doc);
    setEditDocDialogOpen(true);
  };

  const handleSaveDocumentEdit = async () => {
    if (!editingDoc) return;
    
    try {
      await workAPI.updateDocument(editingDoc.id, {
        title: editingDoc.title,
        documentType: editingDoc.documentType,
        summary: editingDoc.summary,
        keyPoints: editingDoc.keyPoints,
        insights: editingDoc.insights,
        source: editingDoc.source
      });
      
      toast({
        title: '✅ Document Updated',
        description: 'Your changes have been saved'
      });
      
      setEditDocDialogOpen(false);
      setEditingDoc(null);
      loadData();
    } catch (error) {
      toast({ title: 'Unable to Update', description: getContextualError(error, 'work.analyzeDocument'), variant: 'destructive' });
    }
  };

  const pendingTasks = tasks.filter(t => t.status === 'pending');
  const completedTasks = tasks.filter(t => t.status === 'completed');
  
  // Enhanced task organization by time
  // Get today's date in local timezone (YYYY-MM-DD format)
  const getTodayDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const getDateDiff = (dueDate) => {
    if (!dueDate) return Infinity;
    
    // Get today's local date string
    const todayStr = getTodayDate(); // Returns "YYYY-MM-DD" in local timezone
    
    // Extract date part from dueDate (could be "YYYY-MM-DD" or "YYYY-MM-DDTHH:MM:SS...")
    const dueStr = typeof dueDate === 'string' ? dueDate.split('T')[0] : dueDate;
    
    // Parse both as local dates for accurate comparison
    const [todayYear, todayMonth, todayDay] = todayStr.split('-').map(Number);
    const [dueYear, dueMonth, dueDay] = dueStr.split('-').map(Number);
    
    const today = new Date(todayYear, todayMonth - 1, todayDay);
    const due = new Date(dueYear, dueMonth - 1, dueDay);
    
    return Math.ceil((due - today) / (1000 * 60 * 60 * 24));
  };
  
  const overdueTasks = pendingTasks.filter(t => t.dueDate && getDateDiff(t.dueDate) < 0);
  const todayTasks = pendingTasks.filter(t => t.dueDate && getDateDiff(t.dueDate) === 0);
  const upcomingTasks = pendingTasks.filter(t => t.dueDate && getDateDiff(t.dueDate) > 0 && getDateDiff(t.dueDate) <= 3);
  const laterTasks = pendingTasks.filter(t => !t.dueDate || getDateDiff(t.dueDate) > 3);
  
  // Calculate completion rate
  const totalTasks = tasks.length;
  const completionRate = totalTasks > 0 ? Math.round((completedTasks.length / totalTasks) * 100) : 0;
  
  // Helper functions for enhanced task display
  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'high': return 'bg-red-100 text-red-700 border-red-300';
      case 'medium': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'low': return 'bg-green-100 text-green-700 border-green-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };
  
  const getPriorityIcon = (priority) => {
    switch(priority) {
      case 'high': return '🔴';
      case 'medium': return '🟡';
      case 'low': return '🟢';
      default: return '⚪';
    }
  };
  
  const getTimeUntilDue = (dueDate, dueTime) => {
    if (!dueDate) return null;
    const diff = getDateDiff(dueDate);
    let timeStr = dueTime ? ` at ${dueTime}` : '';
    
    if (diff < 0) return `${Math.abs(diff)} day${Math.abs(diff) > 1 ? 's' : ''} overdue`;
    if (diff === 0) return dueTime ? `Due today${timeStr}` : 'Due today';
    if (diff === 1) return `Due tomorrow${timeStr}`;
    return `${diff} days left`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50/80 via-cyan-50/60 to-white relative overflow-x-hidden overflow-y-auto">
      <header className="glass-strong border-b border-white/40 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/home')} className="hover:bg-teal-100/60 transition-all duration-200">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center text-white text-xl shadow-soft">
                  💼
                </div>
                <div>
                  <h1 className="text-xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">{t('work.title')}</h1>
                  <p className="text-xs text-gray-500 font-medium">{t('cards.workDesc')} ✨</p>
                </div>
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-10 w-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center">
                  <User className="h-6 w-6 text-gray-700" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-80" align="end">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex items-center space-x-3 p-2">
                    <div className="h-9 w-9 rounded-full bg-teal-600 flex items-center justify-center text-white font-bold text-sm">
                      {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-900">{user?.name}</p>
                      <p className="text-xs text-gray-500">{user?.email}</p>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                
                {subscription && (
                  <>
                    <div className="px-3 py-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">{t('work.subscription')}</span>
                        {subscription.subscription?.plan_id !== 'free' ? (
                          <div className="flex items-center gap-1 bg-teal-100 text-teal-800 px-2 py-1 rounded-full">
                            <Crown className="h-3 w-3" />
                            <span className="text-xs font-medium">{t('work.pro')}</span>
                          </div>
                        ) : (
                          <Badge variant="outline" className="text-xs">{t('work.freePlan')}</Badge>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">{t('work.aiAccess')}</span>
                        {subscription.subscription?.plan_id !== 'free' ? (
                          <div className="flex items-center gap-1">
                            <Sparkles className="h-3 w-3 text-teal-600" />
                            <span className="text-sm font-semibold text-teal-600">{t('work.unlimited')}</span>
                          </div>
                        ) : (
                          <span className="text-sm font-semibold text-gray-900">{subscription.credits || 0} {t('work.credits')}</span>
                        )}
                      </div>
                      {subscription.subscription?.plan_id === 'free' && (
                        <Button
                          onClick={() => navigate('/pricing')}
                          size="sm"
                          className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700"
                        >
                          <Sparkles className="h-3 w-3 mr-2" />
                          {t('work.upgradeToPro')}
                        </Button>
                      )}
                    </div>
                    <DropdownMenuSeparator />
                  </>
                )}
                
                <DropdownMenuItem onClick={() => setShowEditProfile(true)}>
                  <User className="mr-2 h-4 w-4" />
                  <span>{t('settings.editProfile')}</span>
                </DropdownMenuItem>
                
                <DropdownMenuItem onClick={() => navigate('/pricing')}>
                  <CreditCard className="mr-2 h-4 w-4" />
                  <span>{t('settings.manageSubscription')}</span>
                </DropdownMenuItem>
                
                <DropdownMenuItem onClick={() => setShowPaymentHistory(true)}>
                  <Receipt className="mr-2 h-4 w-4" />
                  <span>{t('settings.paymentHistory')}</span>
                </DropdownMenuItem>
                
                <DropdownMenuSeparator />
                
                <DropdownMenuItem onClick={logout} className="text-red-600">
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>{t('settings.logOut')}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            {/* Modals */}
            <EditProfileModal
              isOpen={showEditProfile}
              onClose={() => setShowEditProfile(false)}
              user={user}
              onUpdate={refreshUser}
            />
            <PaymentHistoryModal
              isOpen={showPaymentHistory}
              onClose={() => setShowPaymentHistory(false)}
            />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* NEW: Today&apos;s Focus Dashboard */}
            <TodaysFocusDashboard 
              tasks={tasks}
              onTaskClick={(task) => {
                setEditingTask(task);
                setNewTask({
                  title: task.title || '',
                  description: task.description || '',
                  priority: task.priority || 'medium',
                  dueDate: task.dueDate ? task.dueDate.split('T')[0] : '',
                  dueTime: task.dueTime || task.due_time || ''
                });
                setTaskDialogOpen(true);
              }}
              onCompleteTask={handleCompleteTask}
            />

            {/* Quick Actions Bar */}
            <div className="flex gap-2 overflow-x-auto pb-1">
              <BatchOperations 
                items={tasks}
                itemType="tasks"
                onRefresh={loadTasks}
              />
              <Button
                onClick={() => setTaskDialogOpen(true)}
                className="flex-shrink-0 bg-blue-600 hover:bg-blue-700 whitespace-nowrap"
              >
                + {t('work.newTask')}
              </Button>
            </div>

            {/* Document Analysis */}
            <Card className="border-2 border-teal-200 bg-gradient-to-br from-teal-50 to-blue-50">
              <CardHeader>
                <CardTitle className="flex items-center text-teal-900">
                  <Sparkles className="h-5 w-5 mr-2" />
                  {t('work.documentAnalysis') || 'Document Analysis'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Dialog open={docDialogOpen} onOpenChange={setDocDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="w-full bg-teal-600 hover:bg-teal-700">
                      <Upload className="h-4 w-4 mr-2" />
                      {t('work.analyzeDocument')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>{t('work.documentAnalysis') || 'Document Analysis'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      {!docSummary ? (
                        <>
                          <div className="space-y-2">
                            <Label className="flex items-center justify-between">
                              <span>{t('work.uploadFile')}</span>
                              {uploadedFileName && (
                                <Badge variant="secondary" className="text-xs">
                                  {selectedFile?.name.match(/\.(mp3|mp4|wav|m4a)$/i) ? '🎵' : '📄'} {uploadedFileName}
                                </Badge>
                              )}
                            </Label>
                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-teal-400 transition-colors">
                              <Input
                                type="file"
                                accept=".pdf,.txt,.doc,.docx,.mp3,.mp4,.wav,.m4a"
                                onChange={handleFileSelect}
                                className="cursor-pointer"
                                id="file-upload"
                              />
                              <div className="mt-2 text-sm text-gray-600">
                                {selectedFile ? (
                                  <div className="flex items-center justify-center space-x-2">
                                    {selectedFile.name.match(/\.(mp3|mp4|wav|m4a)$/i) ? (
                                      <Music className="h-5 w-5 text-teal-600" />
                                    ) : (
                                      <FileText className="h-5 w-5 text-teal-600" />
                                    )}
                                    <span className="font-medium">{selectedFile.name}</span>
                                    <span className="text-gray-400">({(selectedFile.size / 1024).toFixed(1)} KB)</span>
                                  </div>
                                ) : (
                                  <p>{t('work.clickToSelect')}</p>
                                )}
                              </div>
                            </div>
                            {selectedFile && selectedFile.name.match(/\.(mp3|mp4|wav|m4a)$/i) && (
                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                                <p className="font-medium">🎤 {t('work.audioVideoDetected')}</p>
                                <p className="text-xs mt-1">{t('work.analyzeWhisper')}</p>
                              </div>
                            )}
                          </div>
                          <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                              <span className="w-full border-t" />
                            </div>
                            <div className="relative flex justify-center text-xs uppercase">
                              <span className="bg-white px-2 text-gray-500">{t('work.or')}</span>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label>{t('work.pasteUrl')}</Label>
                            <Input
                              placeholder="https://example.com/article or blog URL"
                              value={urlInput}
                              onChange={(e) => setUrlInput(e.target.value)}
                              className="font-mono text-sm"
                            />
                            <p className="text-xs text-gray-500">
                              📎 Articles, Blogs, Google Docs - AI will extract content automatically
                            </p>
                          </div>
                          <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                              <span className="w-full border-t" />
                            </div>
                            <div className="relative flex justify-center text-xs uppercase">
                              <span className="bg-white px-2 text-gray-500">{t('work.or')}</span>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label>{t('work.documentTextOptional')}</Label>
                            <Textarea
                              placeholder={t('work.pasteText')}
                              value={documentText}
                              onChange={(e) => setDocumentText(e.target.value)}
                              rows={6}
                              className="font-mono text-sm"
                            />
                          </div>
                          {/* NEW: Template Selector */}
                          <TemplateSelector 
                            value={selectedTemplate}
                            onChange={setSelectedTemplate}
                          />
                          <Button 
                            onClick={handleProcessDocument} 
                            disabled={processingDoc || (!documentText.trim() && !selectedFile && !urlInput.trim())}
                            className="w-full bg-teal-600 hover:bg-teal-700"
                          >
                            {processingDoc ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                {t('work.analyzing')}
                              </>
                            ) : (
                              <>
                                <Sparkles className="h-4 w-4 mr-2" />
                                {t('work.analyze')}
                              </>
                            )}
                          </Button>
                        </>
                      ) : (
                        <>
                          {/* AI Analysis Results */}
                          <div className="space-y-4">
                            {/* Formatted Analysis Display */}
                            <div className="space-y-4">
                              {(() => {
                                // Get the response text from docSummary
                                const responseText = typeof docSummary === 'string' ? docSummary : docSummary?.response || '';
                                
                                // Parse the AI response into sections
                                const summaryMatch = responseText.match(/\*\*Summary:\*\*\s*([\s\S]*?)(?=\*\*(?:Key Points|Insights|Micro Tasks):|$)/i);
                                const keyPointsMatch = responseText.match(/\*\*Key Points:\*\*\s*([\s\S]*?)(?=\*\*(?:Insights|Micro Tasks):|$)/i);
                                const insightsMatch = responseText.match(/\*\*Insights:\*\*\s*([\s\S]*?)(?=\*\*(?:Micro Tasks):|$)/i);
                                const tasksMatch = responseText.match(/\*\*Micro Tasks:\*\*\s*([\s\S]*?)(?=\*\*|$)/i);
                                
                                return (
                                  <>
                                    {/* Summary Section */}
                                    {summaryMatch && (
                                      <div className="bg-gradient-to-br from-teal-50 to-teal-100 border-l-4 border-teal-500 rounded-lg p-4">
                                        <div className="flex items-start gap-3">
                                          <div className="flex-shrink-0 w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center">
                                            <FileText className="h-5 w-5 text-white" />
                                          </div>
                                          <div className="flex-1">
                                            <h3 className="text-lg font-semibold text-teal-900 mb-2">{t('work.summary')}</h3>
                                            <p className="text-gray-700 leading-relaxed">
                                              {summaryMatch[1].trim()}
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Key Points Section */}
                                    {keyPointsMatch && (
                                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-l-4 border-blue-500 rounded-lg p-4">
                                        <div className="flex items-start gap-3">
                                          <div className="flex-shrink-0 w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                                            <CheckCircle className="h-5 w-5 text-white" />
                                          </div>
                                          <div className="flex-1">
                                            <h3 className="text-lg font-semibold text-blue-900 mb-3">{t('work.keyPoints')}</h3>
                                            <ul className="space-y-2">
                                              {keyPointsMatch[1]
                                                .split('\n')
                                                .filter(line => line.trim().startsWith('-') || line.trim().match(/^\d+\./))
                                                .map((point, idx) => (
                                                  <li key={idx} className="flex items-start gap-2">
                                                    <span className="flex-shrink-0 w-6 h-6 bg-blue-200 rounded-full flex items-center justify-center text-xs font-semibold text-blue-700">
                                                      {idx + 1}
                                                    </span>
                                                    <span className="text-gray-700 pt-0.5">
                                                      {point.replace(/^[-\d.]\s*/, '').trim()}
                                                    </span>
                                                  </li>
                                                ))}
                                            </ul>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Insights Section */}
                                    {insightsMatch && (
                                      <div className="bg-gradient-to-br from-amber-50 to-amber-100 border-l-4 border-amber-500 rounded-lg p-4">
                                        <div className="flex items-start gap-3">
                                          <div className="flex-shrink-0 w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center">
                                            <Sparkles className="h-5 w-5 text-white" />
                                          </div>
                                          <div className="flex-1">
                                            <h3 className="text-lg font-semibold text-amber-900 mb-2">{t('work.insights')}</h3>
                                            <div className="text-gray-700 leading-relaxed space-y-2">
                                              {insightsMatch[1]
                                                .split('\n')
                                                .filter(line => line.trim())
                                                .map((insight, idx) => (
                                                  <p key={idx}>
                                                    {insight.replace(/^[-\d.]\s*/, '').trim()}
                                                  </p>
                                                ))}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Micro Tasks Section */}
                                    {tasksMatch && (
                                      <div className="bg-gradient-to-br from-green-50 to-green-100 border-l-4 border-green-500 rounded-lg p-4">
                                        <div className="flex items-start gap-3">
                                          <div className="flex-shrink-0 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                                            <ListTodo className="h-5 w-5 text-white" />
                                          </div>
                                          <div className="flex-1">
                                            <h3 className="text-lg font-semibold text-green-900 mb-3">{t('work.suggestedTasks')}</h3>
                                            <ul className="space-y-2">
                                              {tasksMatch[1]
                                                .split('\n')
                                                .filter(line => line.trim().startsWith('-') || line.trim().match(/^\d+\./))
                                                .map((task, idx) => (
                                                  <li key={idx} className="flex items-start gap-2 bg-white rounded p-2 hover:shadow-sm transition-shadow">
                                                    <input 
                                                      type="checkbox" 
                                                      className="mt-1 rounded border-gray-300"
                                                      disabled
                                                    />
                                                    <span className="text-gray-700 flex-1">
                                                      {task.replace(/^[-\d.]\s*/, '').trim()}
                                                    </span>
                                                  </li>
                                                ))}
                                            </ul>
                                            <p className="text-xs text-green-700 mt-3 italic">
                                              💡 {t('work.generateTasks')}
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Fallback if no sections detected */}
                                    {!summaryMatch && !keyPointsMatch && !insightsMatch && !tasksMatch && (
                                      <div className="bg-gradient-to-br from-gray-50 to-gray-100 border-l-4 border-gray-400 rounded-lg p-4">
                                        <div className="whitespace-pre-wrap text-gray-700">
                                          {typeof docSummary === 'string' ? docSummary : docSummary?.response || ''}
                                        </div>
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                            
                            {/* Action Buttons */}
                            <div className="grid grid-cols-2 gap-3">
                              <Button 
                                onClick={handleGenerateMicrotasks}
                                variant="outline"
                                className="border-teal-300 hover:bg-teal-50"
                              >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                {t('work.generateTasks')}
                              </Button>
                              <Button 
                                onClick={() => setShowQA(!showQA)}
                                variant="outline"
                                className="border-blue-300 hover:bg-blue-50"
                              >
                                <Sparkles className="h-4 w-4 mr-2" />
                                {t('work.askQuestion')}
                              </Button>
                            </div>
                            
                            {/* Full Transcript/Content Section */}
                            {docSummary?.original_content && (
                              <div className="mt-4">
                                <Button
                                  onClick={() => setShowFullTranscript(!showFullTranscript)}
                                  variant="outline"
                                  className="w-full border-gray-300 hover:bg-gray-50 justify-between"
                                >
                                  <span className="flex items-center">
                                    <FileText className="h-4 w-4 mr-2" />
                                    {showFullTranscript ? 'Hide' : 'View'} Full Content/Transcript
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    ({docSummary?.word_count || 0} words)
                                  </span>
                                </Button>
                                
                                {showFullTranscript && (
                                  <div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-4 max-h-96 overflow-y-auto">
                                    <div className="flex justify-between items-center mb-2">
                                      <h4 className="text-sm font-semibold text-gray-700">📄 Original Extracted Content</h4>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => {
                                          navigator.clipboard.writeText(docSummary.original_content);
                                          toast({
                                            title: '✅ Copied!',
                                            description: 'Full content copied to clipboard'
                                          });
                                        }}
                                        className="text-xs"
                                      >
                                        📋 Copy
                                      </Button>
                                    </div>
                                    <div className="whitespace-pre-wrap text-sm text-gray-700 font-mono bg-white p-3 rounded border">
                                      {docSummary.original_content}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Q&A Section */}
                            {showQA && (
                              <div className="space-y-3 bg-blue-50 p-4 rounded-lg border border-blue-200">
                                <Label className="text-sm font-medium">{t('work.askAnythingDoc')}</Label>
                                <Textarea
                                  placeholder="E.g., What are the main action items? Can you explain the methodology? What's the timeline?"
                                  value={qaQuestion}
                                  onChange={(e) => setQaQuestion(e.target.value)}
                                  rows={2}
                                  className="bg-white"
                                />
                                <Button
                                  onClick={handleAskQuestion}
                                  disabled={processingQA || !qaQuestion.trim()}
                                  size="sm"
                                  className="w-full bg-blue-600 hover:bg-blue-700"
                                >
                                  {processingQA ? (
                                    <>
                                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                      Thinking...
                                    </>
                                  ) : (
                                    <>
                                      <Sparkles className="h-4 w-4 mr-2" />
                                      Get Answer
                                    </>
                                  )}
                                </Button>
                                
                                {/* Q&A History */}
                                {qaHistory.length > 0 && (
                                  <div className="mt-4 space-y-3">
                                    {qaHistory.map((qa, index) => (
                                      <div key={index} className="bg-white p-3 rounded border">
                                        <p className="text-sm font-medium text-gray-700 mb-1">
                                          Q: {qa.question}
                                        </p>
                                        <p className="text-sm text-gray-600 whitespace-pre-wrap">
                                          A: {qa.answer}
                                        </p>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Navigation Buttons */}
                            <div className="flex space-x-2 pt-4 border-t">
                              <Button 
                                onClick={() => {
                                  setDocSummary(null);
                                  setDocumentText('');
                                  setUrlInput('');
                                  setSelectedFile(null);
                                  setUploadedFileName('');
                                  setShowQA(false);
                                  setQaHistory([]);
                                  setCurrentAnalyzedDoc(null);
                                }}
                                variant="outline"
                                className="flex-1"
                              >
                                Analyze Another
                              </Button>
                              <Button 
                                onClick={async () => {
                                  // Save to document library before closing
                                  try {
                                    const docTitle = uploadedFileName || urlInput || 'Document Analysis';
                                    const docType = selectedFile ? 'file' : (urlInput ? 'url' : 'text');
                                    const docSource = selectedFile ? uploadedFileName : (urlInput || 'Direct text input');
                                    
                                    // Extract summary from docSummary (can be string or object)
                                    const summaryText = typeof docSummary === 'string' 
                                      ? docSummary 
                                      : (docSummary?.response || docSummary?.summary || docSummary?.analysis || '');
                                    const keyPointsList = docSummary?.action_items?.map(a => a.task || a.description || a) || [];
                                    const originalContent = docSummary?.original_content || documentText || '';
                                    
                                    console.log('Saving document:', { docTitle, summaryText: summaryText.substring(0, 100), keyPointsList });
                                    
                                    const saveData = {
                                      title: docTitle,
                                      documentType: docType,
                                      summary: summaryText,
                                      keyPoints: keyPointsList,
                                      insights: typeof docSummary === 'object' ? (docSummary?.decisions?.join(', ') || '') : '',
                                      source: docSource,
                                      original_content: originalContent,
                                      analysis_result: summaryText
                                    };
                                    
                                    console.log('Save data:', JSON.stringify(saveData).substring(0, 200));
                                    
                                    await workAPI.saveDocument(saveData);
                                    
                                    toast({
                                      title: '✅ Saved to Library',
                                      description: 'Document analysis saved successfully'
                                    });
                                    
                                    // Reload documents
                                    loadDocuments();
                                  } catch (err) {
                                    console.error('Error saving document:', err);
                                    toast({
                                      title: 'Warning',
                                      description: 'Could not save to library',
                                      variant: 'destructive'
                                    });
                                  }
                                  
                                  // Close dialog
                                  setDocDialogOpen(false);
                                  setDocSummary(null);
                                  setDocumentText('');
                                  setUrlInput('');
                                  setSelectedFile(null);
                                  setUploadedFileName('');
                                  setShowQA(false);
                                  setQaHistory([]);
                                  setCurrentAnalyzedDoc(null);
                                }}
                                className="flex-1 bg-green-600 hover:bg-green-700"
                              >
                                Save & Done
                              </Button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
                <p className="text-sm text-teal-700 mt-2">
                  ✨ {t('work.uploadDescription') || 'Upload MP3/MP4 recordings, PDF documents, paste article URLs, or enter text for instant AI analysis & task generation'}
                </p>
              </CardContent>
            </Card>

            {/* Document Library */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center">
                    <FileText className="h-5 w-5 mr-2 text-teal-600" />
                    {t('work.documentLibrary')}
                  </CardTitle>
                  {/* NEW: Action Buttons */}
                  {documents.length > 0 && (
                    <div className="flex gap-2">
                      <Button 
                        variant="outline"
                        size="sm"
                        onClick={() => setShowSearchModal(true)}
                        className="border-teal-300 hover:bg-teal-50"
                      >
                        <Search className="h-4 w-4 mr-2" />
                        {t('common.search') || 'Search'}
                      </Button>
                      {documents.length >= 2 && (
                        <Button 
                          variant="outline"
                          size="sm"
                          onClick={() => setShowCompareModal(true)}
                          className="border-blue-300 hover:bg-blue-50"
                        >
                          <ArrowLeftRight className="h-4 w-4 mr-2" />
                          {t('work.compare') || 'Compare'}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {documents.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>{t('work.noDocumentsYet') || 'No documents analyzed yet'}</p>
                    <p className="text-xs mt-2">{t('work.useDocumentAnalysis') || 'Use Document Analysis above to analyze files'}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {documents.map((doc) => (
                      <div key={doc.id} className="p-4 border-2 border-gray-200 rounded-lg hover:shadow-sm transition-shadow">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge variant="secondary" className="text-xs">
                                {doc.documentType === 'file' ? '📄' : doc.documentType === 'url' ? '🔗' : '📝'}
                              </Badge>
                              <h3 className="font-medium text-gray-900">{doc.title}</h3>
                            </div>
                            {doc.source && (
                              <p className="text-xs text-gray-500 mb-2">Source: {doc.source}</p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleEditDocument(doc)}
                              className="text-blue-600 hover:bg-blue-50"
                              title="View/Edit"
                            >
                              <FileText className="h-4 w-4" />
                            </Button>
                            {/* NEW: Export Button */}
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => {
                                setExportingDoc(doc);
                                setShowExportModal(true);
                              }}
                              className="text-green-600 hover:bg-green-50"
                              title="Export"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleDeleteDocument(doc)}
                              className="text-red-600 hover:bg-red-50"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        
                        {/* Summary */}
                        <div className="mb-3">
                          <p className="text-xs font-semibold text-gray-700 mb-1">Summary:</p>
                          <p className="text-sm text-gray-600 line-clamp-3">{doc.summary || doc.analysis_result || 'No summary available'}</p>
                        </div>
                        
                        {/* Key Points */}
                        {doc.keyPoints && doc.keyPoints.length > 0 && (
                          <div className="mb-3">
                            <p className="text-xs font-semibold text-gray-700 mb-1">Key Points:</p>
                            <ul className="space-y-1">
                              {doc.keyPoints.slice(0, 3).map((point, idx) => (
                                <li key={idx} className="text-xs text-gray-600 flex items-start">
                                  <span className="mr-2">•</span>
                                  <span className="line-clamp-1">{point}</span>
                                </li>
                              ))}
                              {doc.keyPoints.length > 3 && (
                                <li className="text-xs text-blue-600">+{doc.keyPoints.length - 3} more</li>
                              )}
                            </ul>
                          </div>
                        )}
                        
                        {/* Insights */}
                        {doc.insights && (
                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1">Insights:</p>
                            <p className="text-xs text-gray-600 line-clamp-2">{doc.insights}</p>
                          </div>
                        )}
                        
                        <p className="text-xs text-gray-400 mt-2">
                          {(() => {
                            const date = new Date(doc.createdAt);
                            return date.toLocaleString(undefined, {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true
                            });
                          })()}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Task Add/Edit Dialog - Separate from card */}
            <Dialog open={taskDialogOpen} onOpenChange={(open) => {
              setTaskDialogOpen(open);
              if (!open) {
                setEditingTask(null);
                setNewTask({ title: '', description: '', priority: 'medium', dueDate: '', dueTime: '' });
              }
            }}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingTask ? t('work.editTask') : t('work.newTask')}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">{t('work.titleRequired')}</Label>
                    <Input
                      id="title"
                      placeholder={t('work.enterTaskTitle')}
                      value={newTask.title}
                      onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description">{t('work.descriptionLabel')}</Label>
                    <Textarea
                      id="description"
                      placeholder={t('work.addDetails')}
                      value={newTask.description}
                      onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                      rows={3}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="priority">{t('work.priority')}</Label>
                      <Select
                        value={newTask.priority}
                        onValueChange={(value) => setNewTask({ ...newTask, priority: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">{t('work.low')}</SelectItem>
                          <SelectItem value="medium">{t('work.medium')}</SelectItem>
                          <SelectItem value="high">{t('work.high')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="dueDate">{t('work.dueDate')}</Label>
                      <Input
                        id="dueDate"
                        type="date"
                        value={newTask.dueDate}
                        onChange={(e) => setNewTask({ ...newTask, dueDate: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="dueTime">{t('work.dueTime') || 'Time'}</Label>
                      <Input
                        id="dueTime"
                        type="time"
                        value={newTask.dueTime}
                        onChange={(e) => setNewTask({ ...newTask, dueTime: e.target.value })}
                        placeholder="HH:MM"
                      />
                    </div>
                  </div>
                  <Button onClick={editingTask ? handleUpdateTask : handleAddTask} className="w-full bg-blue-600 hover:bg-blue-700">
                    {editingTask ? t('work.updateTask') : t('work.createTask')}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Right Sidebar - Removed per user request */}
          <div className="lg:col-span-1 space-y-6">
            {/* Productivity Analytics and Filters removed */}
          </div>
        </div>
      </main>

      {/* View/Edit Document Dialog */}
      <Dialog open={editDocDialogOpen} onOpenChange={setEditDocDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              {editingDoc?.title || 'Document Analysis'}
            </DialogTitle>
          </DialogHeader>
          {editingDoc && (
            <div className="space-y-4 py-2">
              {/* Source Info */}
              <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 p-2 rounded">
                <span>📄 {editingDoc.documentType || 'document'}</span>
                <span>•</span>
                <span>{editingDoc.source || 'Unknown source'}</span>
                <span>•</span>
                <span>{new Date(editingDoc.createdAt).toLocaleString()}</span>
              </div>
              
              {/* AI Analysis Summary */}
              <div className="bg-gradient-to-r from-blue-50 to-teal-50 rounded-lg p-4 border border-blue-100">
                <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-teal-600" />
                  AI Analysis
                </h4>
                <div className="text-sm text-gray-700 whitespace-pre-wrap max-h-[300px] overflow-y-auto">
                  {editingDoc.summary || editingDoc.analysis_result || 'No analysis available'}
                </div>
              </div>
              
              {/* Key Points */}
              {editingDoc.keyPoints && editingDoc.keyPoints.length > 0 && (
                <div className="bg-green-50 rounded-lg p-4 border border-green-100">
                  <h4 className="font-semibold text-gray-800 mb-2">📌 Key Points</h4>
                  <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                    {editingDoc.keyPoints.map((point, idx) => (
                      <li key={idx}>{point}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Insights */}
              {editingDoc.insights && (
                <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-100">
                  <h4 className="font-semibold text-gray-800 mb-2">💡 Insights</h4>
                  <p className="text-sm text-gray-700">{editingDoc.insights}</p>
                </div>
              )}
              
              {/* Actions */}
              <div className="flex gap-2 justify-end pt-2 border-t">
                <Button
                  onClick={() => setEditDocDialogOpen(false)}
                  variant="outline"
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* NEW: Export Modal */}
      {exportingDoc && (
        <ExportModal
          isOpen={showExportModal}
          onClose={() => {
            setShowExportModal(false);
            setExportingDoc(null);
          }}
          documentId={exportingDoc.id}
          documentTitle={exportingDoc.title}
        />
      )}

      {/* NEW: Document Compare Modal */}
      <DocumentCompareModal
        isOpen={showCompareModal}
        onClose={() => {
          setShowCompareModal(false);
          setSelectedDocsForCompare([]);
        }}
        documents={documents}
        selectedDocs={selectedDocsForCompare}
        setSelectedDocs={setSelectedDocsForCompare}
      />

      {/* NEW: Document Search Dialog */}
      <Dialog open={showSearchModal} onOpenChange={setShowSearchModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Search className="h-5 w-5 text-teal-600" />
              Search Documents
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <DocumentSearch
              onResultClick={(docId) => {
                const doc = documents.find(d => d.id === docId);
                if (doc) {
                  handleEditDocument(doc);
                  setShowSearchModal(false);
                }
              }}
            />
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Quick Action Button - Available on All Pages */}
      <QuickActionButton />
    </div>
  );
};

export default WorkMode;
